'use client';

import { useRef, useEffect, useState, useMemo } from 'react';
import { Canvas, useFrame, extend } from '@react-three/fiber';
import { shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';
import type { ShaderMaterial as ThreeShaderMaterial } from 'three';

// Create custom shader material using drei
const GradientShaderMaterial = shaderMaterial(
  // Uniforms
  {
          uTime: 0,
          uMouse: new THREE.Vector2(0.75, 0.4),
          uAspect: 1.0,
          uBlobOpacity: 0.0,
          uBreathing: 1.0,
          uScale: 1.0,
          uColorVariation: 0.0,
  },
  // Vertex Shader
  `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader
  `
          uniform float uTime;
          uniform vec2 uMouse;
          uniform float uAspect;
          uniform float uBlobOpacity;
          uniform float uBreathing;
          uniform float uScale;
          uniform float uColorVariation;
          varying vec2 vUv;

    // Clean color palette matching the design
    vec3 color1 = vec3(0.55, 0.99, 0.59); // #8DFF96 - bright green
    vec3 color2 = vec3(0.61, 0.87, 0.79); // #9BDECA - teal  
    vec3 colorBlobDarkest = vec3(0.08, 0.25, 0.65); // Even darker blue for breathing in
    vec3 colorBlobDark = vec3(0.25, 0.45, 0.85); // Medium-dark blue for blob center
    vec3 colorBlobLight = vec3(0.55, 0.76, 1.0);  // Light blue for blob edges

    // Subtle noise for organic feel
    float noise(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    float smoothNoise(vec2 st) {
      vec2 i = floor(st);
      vec2 f = fract(st);
      
      float a = noise(i);
      float b = noise(i + vec2(1.0, 0.0));
      float c = noise(i + vec2(0.0, 1.0));
      float d = noise(i + vec2(1.0, 1.0));
      
      vec2 u = f * f * (3.0 - 2.0 * f);
      
      return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
    }

    void main() {
      vec2 st = vUv;
      
      // Create a smooth horizontal gradient as base (green to teal)
      float horizontalGradient = st.x;
      
      // Add very subtle animated noise for organic feel
      vec2 animatedSt = st + vec2(uTime * 0.02, uTime * 0.01);
      float subtleNoise = smoothNoise(animatedSt * 3.0) * 0.08;
      
      // Base gradient mix
      float baseMix = horizontalGradient + subtleNoise;
      
      // Create base color (green to teal gradient)
      vec3 baseColor = mix(color1, color2, baseMix);
      
      // Create soft blue blob that follows mouse - WITH aspect correction
      vec2 aspectSt = st;
      aspectSt.x *= uAspect; // Adjust x coordinate by aspect ratio
      
      vec2 aspectMouse = uMouse;
      aspectMouse.x *= uAspect; // Adjust mouse x coordinate by aspect ratio
      
      float mouseDistance = distance(aspectSt, aspectMouse);
      
      // More concentrated blob with very soft falloff (like the reference)
      // Apply scaling animation to the blob size
      float blobCore = 0.12 * uScale; // Smaller, more concentrated core with scaling
      float blobSoftness = 0.6 * uScale; // Very soft edges with scaling
      float blobIntensity = smoothstep(blobCore + blobSoftness, blobCore, mouseDistance);
      
      // Add very subtle animated distortion for organic feel
      float distortion = smoothNoise(aspectSt * 3.0 + uTime * 0.1) * 0.05;
      blobIntensity *= (1.0 + distortion);
      
      // Create softer gradient falloff with exponential curve
      blobIntensity = pow(blobIntensity, 1.2);
      
      // Create gradient within the blob with breathing animation
      // Breathing makes the center darker and lighter
      // Add color variation to create different shades of dark blue each cycle
      vec3 colorVariant = vec3(0.08 + uColorVariation * 0.12, 0.25 + uColorVariation * 0.08, 0.65 - uColorVariation * 0.08);
      // Mix between dark and darkest, keeping it in the darker range
      vec3 breathingDark = mix(colorBlobDark, colorBlobDarkest, uBreathing * 0.8);
      // Add subtle color variation
      breathingDark = mix(breathingDark, colorVariant, uColorVariation * 0.4);
      // Mix with light edges, but keep center mostly dark
      vec3 blobColor = mix(colorBlobLight, breathingDark, blobIntensity * 0.95);
      
      // Mix the base gradient with the blob gradient (controlled by opacity)
      vec3 finalColor = mix(baseColor, blobColor, blobIntensity * 0.85 * uBlobOpacity);
      
      // Add subtle outer glow with light blue for depth
      float outerGlow = smoothstep(blobCore + blobSoftness * 1.5, blobCore, mouseDistance);
      outerGlow = pow(outerGlow, 2.5);
      finalColor += colorBlobLight * outerGlow * 0.2 * uBlobOpacity;
      
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
);

// Extend drei's material to make it available in JSX
extend({ GradientShaderMaterial });

// TypeScript types for the custom material
type GradientShaderMaterialType = ThreeShaderMaterial & {
  uTime: number;
  uMouse: THREE.Vector2;
  uAspect: number;
  uBlobOpacity: number;
  uBreathing: number;
  uScale: number;
  uColorVariation: number;
};

const Scene = ({ mousePosition }: { mousePosition: { x: number; y: number } }) => {
  const materialRef = useRef<GradientShaderMaterialType>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (materialRef.current) {
      const time = state.clock.elapsedTime;
      materialRef.current.uTime = time;
      
      // === PERLIN NOISE MOVEMENT ===
      // Use multiple octaves of sine/cosine waves at different frequencies
      // to approximate Perlin noise for more organic, unpredictable movement
      
      // First octave - large, slow movements
      const floatX1 = Math.sin(time * 0.3) * 0.025;
      const floatY1 = Math.cos(time * 0.37) * 0.022;
      
      // Second octave - medium movements at different phase
      const floatX2 = Math.sin(time * 0.53 + 10.0) * 0.015;
      const floatY2 = Math.cos(time * 0.61 + 20.0) * 0.013;
      
      // Third octave - small, faster movements for detail
      const floatX3 = Math.sin(time * 0.89 + 30.0) * 0.008;
      const floatY3 = Math.cos(time * 1.03 + 40.0) * 0.007;
      
      // Fourth octave - very subtle high-frequency variation
      const floatX4 = Math.sin(time * 1.47 + 50.0) * 0.004;
      const floatY4 = Math.cos(time * 1.71 + 60.0) * 0.003;
      
      // Combine all octaves for complex, organic Perlin-like movement
      const floatX = floatX1 + floatX2 + floatX3 + floatX4;
      const floatY = floatY1 + floatY2 + floatY3 + floatY4;
      
      // Combine mouse position with Perlin-like floating animation
      const finalX = mousePosition.x + floatX;
      const finalY = mousePosition.y + floatY;
      
      // Invert Y because screen coords go down, UV coords go up
      materialRef.current.uMouse = new THREE.Vector2(finalX, 1.0 - finalY);
      materialRef.current.uAspect = state.viewport.width / state.viewport.height;
      
      // Blob is always visible (opacity = 1.0)
      materialRef.current.uBlobOpacity = 1.0;
      
      // Breathing animation - oscillates between 0 and 1
      // This makes the blob's center pulse darker and lighter
      const breathing = (Math.sin(time * 1.8) + 1.0) * 0.5; // Oscillates 0 to 1, even faster
      materialRef.current.uBreathing = breathing;
      
      // Scaling animation - more pronounced pulsing size with faster speed
      // Oscillates between 0.85 and 1.15 for a noticeable breathing effect
      const scale = 1.0 + Math.sin(time * 2.0) * 0.15;
      materialRef.current.uScale = scale;
      
      // Color variation - slowly varies to create different dark blue shades
      // Uses a slower frequency so it changes over multiple breathing cycles
      const colorVariation = (Math.sin(time * 0.4) + 1.0) * 0.5; // Slow variation
      materialRef.current.uColorVariation = colorVariation;
    }
    
    // Scale mesh to exactly fill the viewport
    if (meshRef.current) {
      meshRef.current.scale.set(state.viewport.width, state.viewport.height, 1);
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 0, 0]}>
      <planeGeometry args={[1, 1]} />
      <primitive object={new GradientShaderMaterial()} ref={materialRef} attach="material" />
    </mesh>
  );
};

interface ShaderBackgroundProps {
  className?: string;
}

export default function ShaderBackground({ className = '' }: ShaderBackgroundProps) {
  // Resting position: slightly left of center, slightly above center
  const restingPosition = useMemo(() => ({ x: 0.45, y: 0.42 }), []);
  const [mousePosition, setMousePosition] = useState(restingPosition);
  const targetPositionRef = useRef(restingPosition);
  const isHoveringRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Only update if we have valid dimensions
        if (rect.width > 0 && rect.height > 0) {
          // Check if mouse is within bounds
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          
          // Update hover state and target position
          if (x >= 0 && x <= 1 && y >= 0 && y <= 1) {
            isHoveringRef.current = true;
            targetPositionRef.current = { x, y };
          } else {
            isHoveringRef.current = false;
            targetPositionRef.current = restingPosition;
          }
        }
      }
    };

    // Listen to mousemove on the window to catch all mouse movements
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [restingPosition]);

  // Smooth animation to target position with easing
  useEffect(() => {
    let animationFrame: number;
    
    // Ease-in-out function for smoother acceleration/deceleration
    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    const animate = () => {
      setMousePosition(current => {
        const dx = targetPositionRef.current.x - current.x;
        const dy = targetPositionRef.current.y - current.y;
        
        // Calculate distance for easing
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Different smoothing based on whether hovering or returning to rest
        // When hovering (following mouse): faster tracking
        // When returning to rest: slower base speed
        // Very lazy follow - very reduced smoothing values for much slower, smoother movement
        const baseSmoothing = isHoveringRef.current ? 0.02 : 0.003;
        
        // Apply easing curve - starts slow, speeds up, then slows down
        const easedProgress = easeInOutCubic(Math.min(distance * 2, 1));
        const smoothing = baseSmoothing * (0.5 + easedProgress * 0.5);
        
        return {
          x: current.x + dx * smoothing,
          y: current.y + dy * smoothing
        };
      });
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []); // Empty dependency array - refs don't cause re-runs

  return (
    <div ref={containerRef} className={`absolute inset-0 w-full h-full ${className}`} style={{ display: 'block', overflow: 'hidden' }}>
      <Canvas
        orthographic
        camera={{ zoom: 1, position: [0, 0, 5] }}
        style={{ width: '100%', height: '100%', display: 'block' }}
        gl={{ alpha: false, antialias: true }}
      >
        <Scene mousePosition={mousePosition} />
      </Canvas>
    </div>
  );
}
